import { Box, Button, Grid, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import { PosMappingsProvider, usePosMappingsContext } from '../context/PosMappingsContext';
import  { PosSkuMapping } from '../components/tabs/PosSkuMapping';  
import  { PosSkuTable } from '../components/tabs/PosSkuTable';  
import { Plus } from '../../../assets';
import PageTitle from '../../../components/shared/PageTitle';
import { PaperPackagingRelations } from '../components/tabs/PaperPackagingRelations';
import POSMappingDeleteModal from './POSMappingDeleteModal';
import ItemMappingModal from './ItemMappingModal';
import { GetPosMappingResponse, GetPaperPackagingResponse } from '../../../app/services/api/generated';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import React from 'react';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { availableCupSize } from '../enums/CupSize'; 
import { MappingType } from '../enums/MappingType';

function PosMappingsInner() {
  const {
    tabs, activeTab, onChange, handleDelete, showDeleteModal, setShowDeleteModal, selectedEntity, showEditModal,
    closeEditModal, showCreateModal, openCreateModal, closeCreateModal, totalCount, page, setPage, maxPages, showResultMessage, setShowResultMessage, searchPOSMapping, isLoading,
  } = usePosMappingsContext();
  const { t } = useTranslation('posMapping');

  const convertEntityToFormData = (entity: GetPosMappingResponse | GetPaperPackagingResponse | undefined, isPaperPackaging: boolean) => {
    if (!entity) return undefined;

    if (isPaperPackaging) {
      const paperEntity = entity as GetPaperPackagingResponse;
      return {
        itemNumber: paperEntity.itemNumber || '', 
        mappingType: MappingType.PaperPackaging,
        itemType: '1', 
        posItemNumber: '', 
        franchise: paperEntity.franchiseNames || [], 
        allStores: paperEntity.stores?.length === 0, 
        stores: paperEntity.stores ? paperEntity.stores.map(s => s.storeName || s.storeNumber || '') : [],
        cupsAndLidsType: paperEntity.cupsLidsType?.toString() || '', 
        paperPackagingType: paperEntity.paperPackagingType?.toString() || '', 
        cupSize: paperEntity.sizes
          ? paperEntity.sizes
            .map(s => {
              const found = availableCupSize.find(opt => opt.key === s);
              return found ? found.value : s.toString();
            })
            .filter(Boolean)
          : [],
        originalCupsAndLidsType: paperEntity.cupsLidsType?.toString() || '',
        originalPaperPackagingType: paperEntity.paperPackagingType?.toString() || '',
      };
    } else {
      const posEntity = entity as GetPosMappingResponse;
      return {
        itemNumber: posEntity.target || '', 
        mappingType: MappingType.POS,
        itemType: posEntity.mappingType?.toString() || '', 
        posItemNumber: posEntity.source || '',
        franchise: posEntity.franchiseNames || [], 
        allStores: posEntity.stores?.length === 0, 
        stores: posEntity.stores ? posEntity.stores.map(s => s.storeName || s.storeNumber || '') : [],
        cupsAndLidsType: '', 
        paperPackagingType: '', 
        cupSize: [], 
        originalPosItemNumber: posEntity.source || '',
      };
    }
  };

  const handleEditSave = () => {
    closeEditModal();
    searchPOSMapping(); 
  };

  const handleCloseCreateModal = () => {
    closeCreateModal();
    searchPOSMapping(); 
  };

  const handleSaveCreateModal = () => {
    closeCreateModal();
    searchPOSMapping(); 
  };

  const handleCloseEditModal = () => {
    closeEditModal();
    searchPOSMapping();
  };

  const handleDeleteCancel = () => setShowDeleteModal(false);

  const handleDeleteConfirm = () => {
    handleDelete();
    setShowDeleteModal(false);
  };

  return (
    <Grid container>
      <Grid item
        xs={12}
        sx={{ textAlign: 'left' }}>
        <PageTitle>{t('itemMapping')}</PageTitle>

      </Grid>
      <Grid 
        item
        xs={12}>
        <Typography variant="textMD"
          sx={{ my: 3 }}>
          {t('itemMappingDescription')}
        </Typography>
      </Grid>
      <Grid item
        xs={12}
        sx={{ mb: 2, mt:6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={activeTab}
            onChange={onChange}
          >
            {tabs.map(tab => (
              <Tab key={tab.key}
                value={tab.key}
                label={tab.label} />
            ))}
          </Tabs>
          <Button
            variant='primary'
            size='lg'
            sx={{ ml: 4 }}    
            onClick={openCreateModal}       
            startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>{t('createNew')}
          </Button>
        </Box>
      </Grid>
      <Grid item
        xs={12}>
        <Box sx={{ mt: 2 }}>
          {isLoading ? (
            <LoadingWheel />
          ) : (
            <>
              {activeTab === MappingType.POS && (
                <>
                  <PosSkuMapping />
                  <PosSkuTable />
                  {totalCount > 0 && (
                    <CustomPagination
                      page={page}
                      setPage={setPage}
                      maxPages={maxPages}
                      breakpointForChangeDisplay={120000}
                    />
                  )}
                </>
              )}
              {activeTab === MappingType.PaperPackaging && <PaperPackagingRelations />}
            </>
          )}
        </Box>
      </Grid>
      <ItemMappingModal open={showCreateModal}
        onClose={handleCloseCreateModal}
        onSave={handleSaveCreateModal}
        setShowResultMessage={setShowResultMessage}
        onTabSwitch={(tabIndex) => onChange(null, tabIndex)} />
      <ItemMappingModal 
        open={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleEditSave}
        editMode={true}
        initialData={convertEntityToFormData(selectedEntity, activeTab === MappingType.PaperPackaging)}
        setShowResultMessage={setShowResultMessage}
      />
      <POSMappingDeleteModal
        open={showDeleteModal}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isPaperPackaging={activeTab === MappingType.PaperPackaging} 
      />
      <MessagesSnackbar
        open={showResultMessage.open}
        onClose={() => setShowResultMessage({ ...showResultMessage, open: false })}
        message={showResultMessage.message}
        severity={showResultMessage.severity}
        duration={showResultMessage.severity === 'error' ? null : 6000}
      />
    </Grid>
  );
}

export default function PosMappingsPage() {
  return (
    <PosMappingsProvider>
      <PosMappingsInner />
    </PosMappingsProvider>
  );
}
